//backend design
import { useState, useEffect } from "react";
import { apiUrl } from "../config/api";
import { useNavigate } from "react-router-dom";

interface Department {
  id: number;
  name: string;
  theme_color: string;
  theme_classes: {
    bgColor: string;
    borderColor: string;
    textColor: string;
    linkBg: string;
  };
  url: string;
  hashtags: string | null;
  title: string | null;
  description: string;
  order: number;
}

function DepartmentsPage() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl("/api/departments"));
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setDepartments(result.data);
      } else {
        setError("API returned unsuccessful response");
      }
    } catch (err) {
      setError(`Failed to load departments: ${err instanceof Error ? err.message : "Unknown error"}`);
      console.error("Fetch error:", err);
      
      // Fallback: Use hardcoded data if API fails
      setDepartments([
        {
          id: 1,
          name: "Enhance Health Services",
          theme_color: "blue",
          theme_classes: {
            bgColor: "bg-blue-100",
            borderColor: "border-blue-500",
            textColor: "text-blue-700",
            linkBg: "bg-blue-500",
          },
          url: "/health",
          hashtags: "health",
          title: "Health Services",
          description: "Building a Foundation for Community Well-being Community health is the cornerstone of sustainable development. In Afghanistan, however, access to quality healthcare remains a critical challenge for many, hindered by poverty, geographic isolation, a shortage of skilled professionals, and under-resourced facilities.",
          order: 1,
        },
        {
          id: 2,
          name: "Quality Education",
          theme_color: "green",
          theme_classes: {
            bgColor: "bg-green-100",
            borderColor: "border-green-500",
            textColor: "text-green-700",
            linkBg: "bg-green-500",
          },
          url: "/education",
          hashtags: "",
          title: null,
          description: "In Afghanistan, profound barriers—including poverty, inadequate infrastructure, and a scarcity of resources—continue to deny many women, children, and youth access to quality learning. This educational deficit not only limits individual potential but also constrains economic participation and the nation's long-term development.",
          order: 2,
        },
        {
          id: 3,
          name: "Empowerment of Women and Children",
          theme_color: "green",
          theme_classes: {
            bgColor: "bg-green-100",
            borderColor: "border-green-500",
            textColor: "text-green-700",
            linkBg: "bg-green-500",
          },
          url: "/women",
          hashtags: "#Networking #Cybersecurity #CloudComputing",
          title: null,
          description: "In Afghanistan, deep-rooted social, cultural, and economic barriers systematically restrict their access to education, healthcare, and economic participation. These constraints stifle individual potential and impede the broader progress of families, communities, and the nation.",
          order: 3,
        },
        {
          id: 4,
          name: "Freelancing & Digital Skills",
          theme_color: "blue",
          theme_classes: {
            bgColor: "bg-blue-100",
            borderColor: "border-blue-500",
            textColor: "text-blue-700",
            linkBg: "bg-blue-500",
          },
          url: "/freelancing",
          hashtags: "#Design #Creativity #UIUX",
          title: null,
          description: "In today's digital economy, freelancing represents a transformative pathway to economic empowerment, particularly for those marginalized from traditional education and employment systems. In Afghanistan, many women and youth face significant social, cultural, and logistical barriers to formal schooling and conventional jobs, severely limiting their prospects for personal growth and income generation.",
          order: 4,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="side block-padding-y overflow-hidden">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-deepspace">
            Empowering Communities Through Four Key Areas
          </h2>
          <p className="text-base md:text-lg text-gray-700 pt-4">
            At NORA, we transform the lives of Afghan communities by creating sustainable opportunities across four vital areas...
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-16">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-6 bg-gray-100 rounded-lg animate-pulse">
              <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6 mb-4"></div>
              <div className="h-8 bg-gray-300 rounded w-24"></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error && departments.length === 0) {
    return (
      <section className="side block-padding-y overflow-hidden">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-deepspace">
            Empowering Communities Through Four Key Areas
          </h2>
          <p className="text-base md:text-lg text-gray-700 pt-4">
            At NORA, we transform the lives of Afghan communities by creating sustainable opportunities across four vital areas...
          </p>
        </div>
        <div className="mt-8 p-6 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">⚠️ {error}</p>
          <p className="text-sm text-gray-600 mt-2">
            Make sure Laravel server is running: <code className="bg-gray-100 px-2 py-1 rounded">php artisan serve</code>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="side block-padding-y overflow-hidden">
      <div>
        <div className="flex justify-between items-start flex-col md:flex-row gap-10 md:gap-4">
          <h2 className="text-3xl md:text-4xl font-bold text-deepspace">
            Empowering Communities Through Four Key Areas
          </h2>
          {departments.length > 0 && (
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {departments.length} departments loaded from database
            </span>
          )}
        </div>
        <p className="text-base md:text-lg text-gray-700 pt-4">
          At NORA, we transform the lives of Afghan communities by creating sustainable opportunities across four vital areas: health, education, digital skills and freelancing, and the empowerment of women and children. Through inclusive, innovative, and targeted programs, we provide pathways for growth, independence, and active participation for everyone.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-16">
        {departments.map((dept) => (
          <div
            key={dept.id}
            className={`pb-10 relative group hover:font-semibold z-10 overflow-hidden mb-6 p-4 ${dept.theme_classes.bgColor} hover:shadow-2xl border-l-4 hover:border-r-4 ${dept.theme_classes.borderColor} rounded-lg transition-all duration-300 ease-in`}
          >
            <h3
              className={`text-2xl font-semibold ${dept.theme_classes.textColor} flex justify-between items-center`}
            >
              <span>{dept.name}</span>
            </h3>
            
            {dept.title && (
              <p className="text-gray-700 my-2 font-bold">{dept.title}</p>
            )}
            
            <p className="text-gray-700 my-2">{dept.description}</p>
            
            <button
              type="button"
              onClick={() => navigate(dept.url)}
              className={`flex items-center justify-between w-fit gap-3 group text-sm text-white ${dept.theme_classes.linkBg} font-bold px-3 py-1 uppercase rounded-full cursor-pointer`}
            >
              more info
              <span className="relative w-6 h-6 flex items-center justify-center">
                <span className="absolute flex h-full w-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6 text-opacity-75 animate-pulse scale-125"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                  />
                </svg>
              </span>
            </button>
            
            {/* Animated background elements */}
            <span
              className={`relative -z-10 bg-blue-500 ${dept.theme_classes.linkBg} animate-circle`}
            ></span>
            <span
              className={`block ${dept.theme_classes.linkBg} animate-circle-to-top`}
            ></span>
            <span
              className={`block ${dept.theme_classes.linkBg} animate-circle-to-left`}
            ></span>
            <span className={`animate-squire-to-topleft ${dept.theme_classes.linkBg}`}></span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default DepartmentsPage;
























































//frontend design
// import { useNavigate } from "react-router-dom";

// const departments = [
//   {
//     name: "Enhance Health Services",
//     bgColor: "bg-blue-100",
//     borderColor: "border-blue-500",
//     textColor: "text-blue-700",
//     linkBg: "bg-blue-500",
//     url: "/health",
//     hashtags: "health",
//     title: "Health Services",
//     description:
//     "Building a Foundation for Community Well-being Community health is the cornerstone of sustainable development. In Afghanistan, however, access to quality healthcare remains a critical challenge for many, hindered by poverty, geographic isolation, a shortage of skilled professionals, and under-resourced facilitie"
      
//   },
//   {
//     name: "Quality Education",
//     bgColor: "bg-green-100",
//     borderColor: "border-green-500",
//     textColor: "text-green-700",
//     linkBg: "bg-green-500",
//     url: "/Education",
//     hashtags: "",
//     description:
//     "In Afghanistan, profound barriers—including poverty, inadequate infrastructure, and a scarcity of resources—continue to deny many women, children, and youth access to quality learning. This educational deficit not only limits individual potential but also constrains economic participation and the nation's long-term development."
//   },
//   {
//     name: "Empowerment of Women and Children",
//     bgColor: "bg-green-100",
//     borderColor: "border-green-500",
//     textColor: "text-green-700",
//     linkBg: "bg-green-500",
//     url: "/Women",
//     hashtags: "#Networking #Cybersecurity #CloudComputing",
//     description:
//       "In Afghanistan, deep-rooted social, cultural, and economic barriers systematically restrict their access to education, healthcare, and economic participation. These constraints stifle individual potential and impede the broader progress of families, communities, and the nation."
//   },
//   {
//     name: "Freelancing & Digital Skills",
//     bgColor: "bg-blue-100",
//     borderColor: "border-blue-500",
//     textColor: "text-blue-700",
//     linkBg: "bg-blue-500",
//     url: "/Freelancing",
//     hashtags: "#Design #Creativity #UIUX",
//     description:
//     "In today’s digital economy, freelancing represents a transformative pathway to economic empowerment, particularly for those marginalized from traditional education and employment systems. In Afghanistan, many women and youth face significant social, cultural, and logistical barriers to formal schooling and conventional jobs, severely limiting their prospects for personal growth and income generation."    
//   },
// ];

// function DepartmentsPage() {
//   const navigate = useNavigate()

 

//   return (
//     <>
      
//       <section className="side block-padding-y overflow-hidden">
//         <div>
//           <div className="flex justify-between items-start flex-col md:flex-row gap-10 md:gap-4">
//             <h2 className="text-3xl md:text-4xl font-bold text-deepspace">
//               Empowering Communities Through Four Key Areas
//             </h2>
            
//           </div>
//           <p className="text-base md:text-lg text-gray-700 pt-4">
//             At NORA, we transform the lives of Afghan communities by creating sustainable opportunities across four vital areas: health, education, digital skills and freelancing, and the empowerment of women and children. Through inclusive, innovative, and targeted programs, we provide pathways for growth, independence, and active participation for everyone.
//           </p>
//         </div>

//         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-16">
//           {departments.map((dept, index) => (
//             <div
//               key={index}
//               className={`pb-10 relative group hover:font-semibold z-10 overflow-hidden mb-6 p-4 ${dept.bgColor} hover:shadow-2xl border-l-4 hover:border-r-4 ${dept.borderColor} rounded-lg transition-all duration-300 ease-in`}
              
//             >
//               <h3
//                 className={`text-2xl font-semibold ${dept.textColor} flex justify-between items-center`}
//               >
//                 <span>{dept.name}</span>
               
//               </h3>
//               <p className="text-gray-700 my-2 font-bold">{dept.title}</p>
//               <p className="text-gray-700 my-2">{dept.description}</p>
//               <button
//                 type="button"
//                 onClick={() => navigate(dept.url)}
//                 className={`flex items-center justify-between w-fit gap-3 group text-sm text-white ${dept.linkBg} font-bold px-3 py-1 uppercase rounded-full cursor-pointer`}
//               >
//                 more info
//                 <span className="relative w-6 h-6 flex items-center justify-center">
//                   <span className="absolute flex h-full w-full">
//                     <svg
//                       xmlns="http://www.w3.org/2000/svg"
//                       fill="none"
//                       viewBox="0 0 24 24"
//                       strokeWidth={1.5}
//                       stroke="currentColor"
//                       className="size-6 text-opacity-75 animate-pulse scale-125"
//                     >
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
//                       />
//                     </svg>
//                   </span>
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                     strokeWidth={1.5}
//                     stroke="currentColor"
//                     className="size-6"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
//                     />
//                   </svg>
//                 </span>
//               </button>
//               <span
//                 className={`relative -z-10 bg-blue-500 ${dept.linkBg} animate-circle`}
//               ></span>
//               <span
//                 className={`block ${dept.linkBg} animate-circle-to-top`}
//               ></span>
//               <span
//                 className={`block ${dept.linkBg} animate-circle-to-left`}
//               ></span>

//               <span className={`animate-squire-to-topleft ${dept.linkBg}`}></span>
//             </div>
//           ))}
//         </div>
//       </section>
//     </>
//   );
// }

// export default DepartmentsPage;
